import React, { Fragment } from 'react';
import { BlockType } from '../../../../types/api/ElementType';
import { AnnouncementInteractions } from '../AnnouncementInteractions/AnnouncementInteractions';
import { Strong } from '../../../../components/atoms/Strong/Strong';
import AnnouncementConfirmForm from '../../forms/AnnouncementConfirmForm/AnnouncementConfirmForm';
import { StatusInfoPositiveCheck } from '../../../../_lib/components/StatusInfo/StatusInfoPositiveCheck';
import { useSonTranslation } from '../../../../translation/useSonTranslation';
import { ContentLink } from '../../../../_lib/components/ContentLink';
import { AnnouncementProps, isRecipientWithoutLink } from '../../Announcement.types';
import { murmurhash } from '../../../../lib/js/hash';
import { StatusInfoModifier } from '../../../../_lib/components/StatusInfo/partials/StatusInfo';

export function useAnnouncement({
  title,
  editModeActive,
  elementType,
  mandatory,
  analysis,
  confirmForm,
  confirmUrl,
  editForm,
  deleteForm,
  confirmed,
  sender,
  recipients,
  confirmationInfo,
}: AnnouncementProps) {
  const { t } = useSonTranslation('Announcement');

  const confirmationInfoText = confirmationInfo
    ? `${confirmationInfo.confirmed} ${t('mandatory.of')} ${confirmationInfo.confirmed + confirmationInfo.unconfirmed}`
    : null;

  const confirmationInfoElement =
    mandatory && confirmationInfo ? (
      <span className="announcement__confirmed font-regular-13-13" aria-hidden="true">
        {confirmationInfoText} {t('mandatory.confirmed')}
      </span>
    ) : null;

  const renderInteractionButtons = () =>
    editModeActive && elementType === BlockType.allannouncementsoutbox ? (
      <AnnouncementInteractions title={title} deleteForm={deleteForm} editForm={editForm} analysis={analysis}>
        {confirmationInfoElement}
      </AnnouncementInteractions>
    ) : (
      confirmationInfoElement
    );

  const renderRecipients = () => {
    if (recipients?.sendToAll) {
      return <Strong>{t('toAll')}</Strong>;
    }

    return recipients?.recipientList
      ? recipients.recipientList.map((recipient, index) => (
          <Fragment key={murmurhash(recipient)}>
            {isRecipientWithoutLink(recipient) ? (
              <Strong key={recipient.name}>{recipient.name}</Strong>
            ) : (
              <ContentLink to={recipient.link.url}>{recipient.link.name}</ContentLink>
            )}
            {index !== recipients.recipientList.length - 1 && ', '}
          </Fragment>
        ))
      : null;
  };

  /**
   * Make sure, there is the correct heading hierarchy (sometimes after <h1>, sometimes <h2>)
   * @returns {number}
   */
  const getHeadlineHierarchy = () => {
    if (elementType === BlockType.allannouncementsoutbox || elementType === BlockType.allannouncementsinbox) {
      return 2;
    }
    return 3;
  };

  const renderSender = () => (sender ? <ContentLink to={sender.url}>{sender.name}</ContentLink> : null);

  /**
   * Returns interactions for mandatory announcements
   * */
  const announcementInteractions = () => {
    /**
     * Contentlink if you can't confirm announcement here, but on the associated page.
     */
    if (confirmUrl) {
      return (
        <ContentLink routing={false} to={confirmUrl}>
          {t('confirmLink')}
        </ContentLink>
      );
    }
    /**
     * Confirm form is returned until it was confirmed by user.
     */
    if (confirmForm && !confirmed) {
      return (
        <div className="announcement__confirm-button">
          <AnnouncementConfirmForm confirmForm={confirmForm} />
        </div>
      );
    }
    /**
     * InteractionStatus, to show the user that he/she has already confirmed it.
     */
    if (!confirmForm && confirmed) {
      // no role should be set here
      // eslint-disable-next-line jsx-a11y/aria-role
      return <StatusInfoPositiveCheck message={t('confirmed')} modifier={StatusInfoModifier.RIGHT} role={null} />;
    }
  };

  return {
    confirmationInfoText,
    renderInteractionButtons,
    renderRecipients,
    getHeadlineHierarchy,
    renderSender,
    announcementInteractions,
  };
}
